import {
  GRADEBOOK_SETTINGS,
  getToolIds,
} from '@macmillan-learning/gradebook-shared';
import filter from 'lodash/filter';
import get from 'lodash/get';
import _isFinite from 'lodash/isFinite';
import _isString from 'lodash/isString';
import React from 'react';

import categoryNameStyles from '../../../../components/CategoryNameButton/CategoryNameButton.module.css';
import { getConfigObject } from '../../../../utils/config';
import {
  GRADING_TYPE,
  ISG_REPORT_VIEW_OPTIONS,
} from '../../../../utils/constants';
import AssignmentHeaderLabelAndTool from '../../../AssignmentHeaderLabelAndTool';
import MLIcon from '../../../MLIcon/MLIcon';
import MLTooltip from '../../../MLTooltip';
import PointOrGrade from '../PointOrGrade';
import s from './GridCell.module.css';

export const getCategoryRowData = ({
  cellData,
  columnIndex,
  isCollapsed,
  leftGridColumnCount,
  gradingType,
  isgCurrentView,
}) => {
  let content = '';
  let isCellExpandable = false;
  let containerStyles = [s.categoryCell];
  const isSingleCell = get(cellData, 'isSingleCell', false);
  const isFirstCell = get(cellData, 'isFirstCell', false);
  const isMiddleCell = get(cellData, 'isMiddleCell', false);
  const isLastCell = get(cellData, 'isLastCell', false);

  if (columnIndex < leftGridColumnCount) {
    // If frozen column cell
    if (columnIndex === 0) containerStyles.push(s.firstColumn);
    if (columnIndex === 0 || leftGridColumnCount > 1)
      containerStyles.push(s.hideRightBorder);
    if (isLastCell) containerStyles.push(s.last);
    content = '';
  } else if (isSingleCell) {
    // If cell only has one assignment
    if (
      (!isgCurrentView ||
        isgCurrentView === ISG_REPORT_VIEW_OPTIONS.CATEGORY) &&
      gradingType === GRADEBOOK_SETTINGS.gradingTypeWeighted
    ) {
      const name = cellData.gradebook_category_name;
      const weight = cellData.weight || 0;
      content = (
        <span className={categoryNameStyles.name}>
          <span>{name}</span>
          {
            <span className={categoryNameStyles.weights}>
              {weight}% of Total
            </span>
          }
        </span>
      );
    } else {
      content = cellData.gradebook_category_name;
    }
    containerStyles.push(s.last);
  } else if (isFirstCell) {
    content = cellData.gradebook_category_name;
    isCellExpandable = true;
    containerStyles.push(s.first);
    if (isCollapsed) containerStyles.push(s.collapsed);
  } else if (isMiddleCell) {
    content = '';
    containerStyles.push(s.middle);
  } else if (isLastCell) {
    content = '';
    if (cellData.id === '0') containerStyles.push(s.last);
  }

  if (cellData && cellData.isAggregateCell)
    containerStyles.push(s.aggregateCell);
  containerStyles = containerStyles.join(' ');

  let testId = '';
  if (content) {
    testId = `gradebook_table_category_cell-${content.toString()}`;
  }
  return { content, containerStyles, testId, isCellExpandable };
};

export const getAssignmentRowData = ({
  cellData,
  columnIndex,
  leftGridColumnCount,
  hoveredColumnIndex,
  user,
  sortOptions,
  getLaunchUrl,
  handleLocalLaunch,
  truncate = true,
  showDueDate = true,
  isIndividualStudentGradebook = false,
  shouldSidePanelBeActive = false,
  isDropdownOpen,
}) => {
  const { FF_SIDE_PANEL_ACTIVE } = getConfigObject();
  let content = get(cellData, 'headerContent', '');
  let containerStyles = [s.assignmentCell];
  const testId = 'gradebook_table_assignments_cell';
  const isCourseTotalColumn = get(cellData, 'isCourseTotalColumn');

  if (columnIndex >= leftGridColumnCount || isCourseTotalColumn) {
    const isCellHovered = columnIndex === hoveredColumnIndex;
    content = (
      <AssignmentHeaderLabelAndTool
        truncate={truncate}
        assignment={cellData}
        user={user}
        sortOptions={sortOptions}
        getLaunchUrl={getLaunchUrl}
        handleLocalLaunch={handleLocalLaunch}
        showDueDate={showDueDate}
        isIndividualStudentGradebook={isIndividualStudentGradebook}
        shouldSidePanelBeActive={shouldSidePanelBeActive}
        isDropdownOpen={isDropdownOpen}
      />
    );
    if (!FF_SIDE_PANEL_ACTIVE && isCellHovered) {
      containerStyles.push(s.hoveredCell);
    }
  }

  if (
    shouldSidePanelBeActive &&
    cellData &&
    !cellData.isAggregateCell &&
    !isCourseTotalColumn
  ) {
    containerStyles.push(s.hoverCell, s.hoverCellPointer);
  }

  if ((cellData && cellData.isAggregateCell) || isCourseTotalColumn) {
    containerStyles.push(s.aggregateCell);
  }

  if (columnIndex === 0) containerStyles.push(s.firstColumn);
  if (sortOptions || isCourseTotalColumn) containerStyles.push(s.fullWidth);
  containerStyles = containerStyles.join(' ');
  return { content, containerStyles, testId, isAssignmentHeaderCell: true };
};

export const getHasDecimalPoints = (pointsPossible) =>
  !isNaN(pointsPossible) && !Number.isInteger(pointsPossible);

export const getAverageRowData = ({
  cellData,
  columnIndex,
  showPercent,
  roundPercentType,
}) => {
  let containerStyles = [s.averageCell];
  let content = '';
  const testId = `col${columnIndex}-gradebook-average`;
  const isAggregateCell = get(cellData, 'isAggregateCell', false);
  const isFirstCell = get(cellData, 'isFirstCell', false);
  const isEmptyCell = get(cellData, 'isEmptyCell', false);

  if (cellData.isUngraded && isAggregateCell) {
    content = '';
  } else if (cellData.isUngraded) {
    const numCompleted = get(cellData, 'numCompleted', 0);
    content = (
      <div
        className={s.ungradedHeaderContainer}
        data-test-id={`${columnIndex}-ungraded-average`}
      >
        <span className={s.ungradedHeaderNumbers}>{numCompleted} </span>
        <span className={s.ungradedDescriptionText}> students completed</span>
      </div>
    );
    containerStyles.push(s.aggregateCell);
  } else if (isFirstCell) {
    content = cellData && cellData.title;
  } else if (isEmptyCell) {
    content = '';
  } else if (isAggregateCell) {
    content = '';
    containerStyles.push(s.aggregateCell);
  } else {
    const grade = get(cellData, 'grade', 0);
    const pointsEarned = get(cellData, 'pointsEarned', 0);
    const pointsPossible = get(cellData, 'pointsPossible', 0);
    const isReefAssignment = get(cellData, 'isReefAssignment');
    const hasDecimalPoints = getHasDecimalPoints(pointsPossible);
    const showPointsDecimals = isReefAssignment || hasDecimalPoints;
    const missing = get(cellData, 'missing', false);
    if (pointsPossible === null) {
      content = <small>Max grade not set</small>;
    } else {
      content = (
        <PointOrGrade
          points={pointsEarned}
          outOfTotal={pointsPossible}
          grade={grade}
          showPercent={showPercent}
          roundPercentType={roundPercentType}
          showOutOf
          showPointsDecimals={showPointsDecimals}
          missing={missing}
        />
      );
    }

    containerStyles.push(s.numericCell);
  }

  if (columnIndex === 0) containerStyles.push(s.firstColumn);
  containerStyles = containerStyles.join(' ');
  return { content, containerStyles, testId };
};

const getStudentEvenOrOddRow = (rowIndex) => {
  if (rowIndex % 2 === 0) return s.evenRow;
  return s.oddRow;
};

const copyToClipboard = (str, event) => {
  event.stopPropagation();
  navigator.clipboard.writeText(str);
};

export const getStudentRowData = ({
  cellData,
  rowIndex,
  columnIndex,
  showPercent,
  roundPercentType,
  hoveredRowIndex,
  hoveredColumnIndex,
  isgCurrentView,
  isMobileTotal,
  displayClass,
}) => {
  const { FF_SIDE_PANEL_ACTIVE } = getConfigObject();
  let content = '';
  let isGradeCell = false;
  let containerStyles = [s.studentCell];
  let testId = '';
  let hasDueDateException = false;
  const isStudentNameColumn = get(cellData, 'isStudentNameColumn');
  const isStudentIdColumn = get(cellData, 'isStudentIdColumn');
  const isCourseTotalColumn = get(cellData, 'isCourseTotalColumn');
  const isAggregateCell = get(cellData, 'isAggregateCell');
  const isAssigned = get(cellData, 'isAssigned', false);
  const numCompleted = get(cellData, 'numCompleted', 0);

  if (isStudentNameColumn) {
    testId = 'student_name';
    const email = get(cellData, 'email', 'unknown');
    const firstName = get(cellData, 'firstName', 'Unknown');
    const lastName = get(cellData, 'lastName', 'Profile');
    const isBrokenSyncReefGrades = get(
      cellData,
      'isBrokenSyncReefGrades',
      false,
    );
    const isCellHovered = rowIndex === hoveredRowIndex;

    if (isBrokenSyncReefGrades) containerStyles.push(s.brokenReefSync);

    content = (
      <div className={s.nameEmailContainer}>
        <div className={s.nameEmailInnerContainer}>
          <div className={s.name} data-test-id={`${firstName}-student-name`}>
            <span>{firstName}</span>
            <span>{lastName}</span>
          </div>
          <MLTooltip
            interactive={true}
            content={
              <div className={s.copyEmailTooltip}>
                <button
                  className={s.copyEmailButton}
                  onClick={(event) => copyToClipboard(email, event)}
                >
                  <MLIcon
                    type="item_copy"
                    role="img"
                    className={s.tooltipIcon}
                  />
                  Copy email to clipboard
                </button>
              </div>
            }
          >
            <div
              className={s.email}
              data-test-id={`${firstName}-student-email`}
            >
              {email}
            </div>
          </MLTooltip>
        </div>
        {isBrokenSyncReefGrades && (
          <div
            className={s.brokenReefSyncContainer}
            data-test-id={`${firstName}-iclicker-disconnected`}
          >
            <MLTooltip
              content={
                <div className={s.iClickerMessage}>
                  iClicker Connection
                  <br />
                  Not Established
                </div>
              }
            >
              <MLIcon
                viewBox="0 0 40 40"
                type="unlink"
                role="presentation"
                className={s.brokenReefSyncIcon}
              />
            </MLTooltip>
          </div>
        )}
      </div>
    );

    containerStyles.push(s.firstColumn);
    if (!FF_SIDE_PANEL_ACTIVE && isCellHovered) {
      containerStyles.push(s.hoveredCell);
    }
  } else if (isCourseTotalColumn) {
    testId = `row${rowIndex}-course-total`;
    const grade = get(cellData, 'grade', 0);
    const pointsEarned = get(cellData, 'pointsEarned', 0);
    const pointsPossible = get(cellData, 'pointsPossible', 0);
    const hasDecimalPoints = getHasDecimalPoints(pointsPossible);

    content = (
      <PointOrGrade
        grade={grade}
        points={pointsEarned}
        outOfTotal={pointsPossible}
        showPercent={showPercent}
        roundPercentType={roundPercentType}
        showOutOf
        showPointsDecimals={hasDecimalPoints}
      />
    );

    containerStyles.push(s.numericCell);
    containerStyles.push(s.aggregateCell);
  } else if (isStudentIdColumn) {
    testId = `row${rowIndex}-student-id`;
    const headerContent = get(cellData, 'headerContent');

    content = <div className={s.lmsId}>{headerContent}</div>;
  } else if (isAggregateCell && cellData.isUngraded) {
    content = (
      <div
        className={s.ungradedHeaderContainer}
        data-test-id={`row${rowIndex}-col${columnIndex}-ungraded-total`}
      >
        <span className={s.ungradedHeaderNumbers}>{numCompleted} </span>
        <span className={s.ungradedDescriptionText}> completed</span>
      </div>
    );
  } else {
    // student assignment grade & category total cells
    testId = `assignment_grade${isAggregateCell ? '_total' : ''}_${
      rowIndex - 2
    }_${columnIndex - 1}`;
    const studentPoints = get(cellData, 'studentPoints', 0);
    const { manual: manualToolId } = getToolIds();
    const arePointsValid = _isFinite(studentPoints) || _isString(studentPoints);
    // In case assignment is manual, check if it's graded to add as possible points
    const shouldAddPossiblePoints =
      get(cellData, 'tool') === manualToolId ? arePointsValid : true;
    const assignedPoints = shouldAddPossiblePoints
      ? get(cellData, 'assigned_points', 0)
      : 0;
    const studentGrade = get(cellData, 'studentGrade', 0);
    const weightedCategoryTotal = get(cellData, 'weightedCategoryTotal', 0);
    const blank = get(cellData, 'blank', false);
    const isCellHovered =
      columnIndex === hoveredColumnIndex && rowIndex === hoveredRowIndex;
    const showUngrade =
      get(cellData, 'grading_type', 1) === GRADING_TYPE.UNGRADED;
    hasDueDateException =
      filter(cellData?.Exceptions ?? [], {
        exception_type: 'due_date',
        user_ids: [get(cellData, 'studentId')],
      }).length > 0;

    content = (
      <PointOrGrade
        grade={studentGrade}
        assignment={cellData}
        points={studentPoints}
        outOfTotal={!isMobileTotal && assignedPoints}
        showPercent={showPercent}
        roundPercentType={roundPercentType}
        showUngrade={showUngrade}
        isTotal={isAggregateCell}
        weightedCategoryTotal={weightedCategoryTotal}
        blank={blank}
        isgCurrentView={isgCurrentView}
        displayClass={displayClass}
      />
    );

    isGradeCell = true;

    if (
      FF_SIDE_PANEL_ACTIVE &&
      cellData &&
      !cellData.isAggregateCell &&
      !isCourseTotalColumn
    ) {
      containerStyles.push(s.hoverCell);
      if (isAssigned) {
        containerStyles.push(s.hoverCellPointer);
      }
    }

    if (!FF_SIDE_PANEL_ACTIVE && isCellHovered) {
      containerStyles.push(s.hoveredCell);
    }
    containerStyles.push(s.numericCell);
  }

  containerStyles.push(getStudentEvenOrOddRow(rowIndex));
  if (isAggregateCell) containerStyles.push(s.aggregateCell);
  containerStyles = containerStyles.join(' ');
  return {
    content,
    isGradeCell,
    containerStyles,
    testId,
    hasDueDateException,
    isStudentNameColumn,
  };
};
